import React, { useState, useEffect } from 'react';
import {
  Smartphone,
  Upload,
  Trash2,
  CheckCircle2,
  Download,
  Loader2,
  Plus,
  FileEdit,
  FileText,
  X
} from 'lucide-react';
import { adminAppVersionsAPI } from '../services/api';

const AdminAppUpdates = () => {
  const [appVersions, setAppVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingVersion, setEditingVersion] = useState(null);
  const [formData, setFormData] = useState({
    version: '',
    release_notes: '',
    is_active: false,
    apk_file: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchAppVersions();
  }, []);

  const fetchAppVersions = async () => {
    try {
      setLoading(true);
      const response = await adminAppVersionsAPI.getAll();
      setAppVersions(response.data);
    } catch (err) {
      setError('Failed to fetch app versions');
      console.error('Error fetching app versions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      setFormData({
        ...formData,
        [name]: files[0]
      });
    } else if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      if (editingVersion) {
        await adminAppVersionsAPI.update(editingVersion.id, formData);
      } else {
        await adminAppVersionsAPI.create(formData);
      }
      
      await fetchAppVersions();
      resetForm();
      alert(editingVersion ? 'App version updated successfully!' : 'App version created successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.errors || err.response?.data?.error || 'Failed to save app version';
      setError(Array.isArray(errorMsg) ? errorMsg.join(', ') : errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this app version?')) {
      return;
    }

    try {
      await adminAppVersionsAPI.delete(id);
      await fetchAppVersions();
      alert('App version deleted successfully!');
    } catch (err) {
      alert('Failed to delete app version');
      console.error('Error deleting app version:', err);
    }
  };

  const handleActivate = async (id) => {
    try {
      await adminAppVersionsAPI.activate(id);
      await fetchAppVersions();
      alert('App version activated successfully!');
    } catch (err) {
      alert('Failed to activate app version');
      console.error('Error activating app version:', err);
    }
  };

  const handleEdit = (version) => {
    setEditingVersion(version);
    setFormData({
      version: version.version,
      release_notes: version.release_notes || '',
      is_active: version.is_active,
      apk_file: null
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      version: '',
      release_notes: '',
      is_active: false,
      apk_file: null
    });
    setEditingVersion(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">App Updates</h1>
              <p className="text-gray-600">Manage mobile app versions and APK files</p>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Plus className="w-5 h-5" />
              <span>New Version</span>
            </button>
          </div>
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-8 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingVersion ? 'Edit App Version' : 'Create New App Version'}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
                <p className="text-red-800">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Version Number *
                  </label>
                  <input
                    type="text"
                    name="version"
                    value={formData.version}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., 1.0.0"
                  />
                </div>

                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Set as Active Version</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Release Notes
                </label>
                <textarea
                  name="release_notes"
                  value={formData.release_notes}
                  onChange={handleInputChange}
                  rows="5"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                  placeholder="Enter release notes for this version..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  APK File (Optional for web-only updates)
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-green-400 transition-colors">
                  <div className="space-y-1 text-center">
                    <FileText className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label className="relative cursor-pointer bg-white rounded-md font-medium text-green-600 hover:text-green-500 focus-within:outline-none">
                        <span>Upload APK file</span>
                        <input
                          type="file"
                          name="apk_file"
                          accept=".apk"
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">APK files only</p>
                    {formData.apk_file && (
                      <p className="text-sm text-green-600 font-medium">
                        Selected: {formData.apk_file.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white py-3 px-6 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span>{editingVersion ? 'Update Version' : 'Create Version'}</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-semibold transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* App Versions List */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
          <div className="px-6 py-4 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">App Versions</h2>
          </div>

          {appVersions.length === 0 ? (
            <div className="p-12 text-center">
              <Smartphone className="text-6xl text-gray-400 mb-4 mx-auto" />
              <p className="text-gray-600 text-lg">No app versions found</p>
              <p className="text-gray-500 text-sm mt-2">Create your first app version to get started</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {appVersions.map((version) => (
                <div
                  key={version.id}
                  className={`p-6 hover:bg-gray-50 transition-colors ${
                    version.is_active ? 'bg-green-50 border-l-4 border-green-500' : ''
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">Version {version.version}</h3>
                        {version.is_active && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                            <CheckCircle2 className="w-3 h-3" />
                            Active
                          </span>
                        )}
                      </div>
                      {version.release_notes && (
                        <p className="text-gray-600 text-sm mb-3 whitespace-pre-wrap">
                          {version.release_notes}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        <span>Downloads: {version.download_count || 0}</span>
                        <span>Created: {new Date(version.created_at).toLocaleDateString()}</span>
                        {version.apk_file_url && (
                          <a
                            href={version.apk_file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                          >
                            <Download className="w-4 h-4" />
                            <span>Download APK</span>
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      {!version.is_active && (
                        <button
                          onClick={() => handleActivate(version.id)}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                        >
                          Activate
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(version)}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2"
                      >
                        <FileEdit className="w-4 h-4" />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => handleDelete(version.id)}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAppUpdates;

